import { useEffect, useRef } from "react";

/**
 * Matrix-style falling hex digit rain rendered onto a <canvas>.
 * Sits as a fixed background behind the grid overlay — very low opacity
 * so it doesn't fight the actual content. Pink to match the rest of the UI.
 *
 * Perf-aware: resizes with window, uses a single requestAnimationFrame loop,
 * and renders sparse columns so it stays well under 1% CPU on a modern laptop.
 */
export function HexRain({
  color = "#ff2d92",
  accent = "#ff86c2",
  fontSize = 14,
  density = 0.55,
  speed = 0.7,
  opacity = 0.13,
}: {
  color?: string;
  accent?: string;
  fontSize?: number;
  density?: number;
  speed?: number;
  opacity?: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let drops: number[] = [];
    let columns = 0;
    let raf = 0;
    const glyphs = "0123456789ABCDEF░▒▓│┤┐└┴┬├─┼╬╦╩╠╣█".split("");

    function setup() {
      const dpr = window.devicePixelRatio || 1;
      const w = window.innerWidth;
      const h = window.innerHeight;
      canvas!.width = Math.floor(w * dpr);
      canvas!.height = Math.floor(h * dpr);
      canvas!.style.width = w + "px";
      canvas!.style.height = h + "px";
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);

      columns = Math.floor(w / fontSize);
      drops = new Array(columns).fill(0).map(
        () => -Math.random() * (h / fontSize)
      );
    }

    function frame() {
      const w = window.innerWidth;
      const h = window.innerHeight;

      // Fade previous frame to create the trailing comet effect.
      ctx!.fillStyle = "rgba(8, 2, 11, 0.08)";
      ctx!.fillRect(0, 0, w, h);

      ctx!.font = `${fontSize}px "JetBrains Mono", monospace`;
      ctx!.textBaseline = "top";

      for (let i = 0; i < columns; i++) {
        // Skip some columns to thin out the rain.
        if (Math.random() > density) continue;

        const ch = glyphs[Math.floor(Math.random() * glyphs.length)];
        const x = i * fontSize;
        const y = drops[i] * fontSize;

        // Head of each stream is brighter, trail is muted.
        ctx!.fillStyle = Math.random() > 0.98 ? accent : color;
        ctx!.fillText(ch, x, y);

        drops[i] += speed;
        if (y > h && Math.random() > 0.975) {
          drops[i] = -Math.random() * 20;
        }
      }

      raf = requestAnimationFrame(frame);
    }

    setup();
    frame();
    window.addEventListener("resize", setup);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", setup);
    };
  }, [color, accent, fontSize, density, speed]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className="pointer-events-none fixed inset-0"
      style={{ opacity, zIndex: 0 }}
    />
  );
}
